/**
 * Start the Day — Morning Operations Dashboard.
 * Version 1.1 · Sprint 2 · PO directive 2026-07-31.
 *
 * The operational heartbeat. Opening `/start-the-day` shows the four
 * morning steps with their current status, and the primary button
 * routes to the next incomplete step. If the operator is mid-workflow
 * the button reads *"Continue Start the Day"*, otherwise
 * *"Start the Day"*.
 *
 * Sprint 10 · 2026-08-06 · PO clarification: the Morning Brief is the
 * *outcome* of the four steps, not a step to complete. It renders as
 * a distinct result card below the checklist and unlocks when all
 * four steps flip to COMPLETE.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SunHorizon, CheckCircle, Circle, CaretRight, DotOutline, Sun,
  Truck, Warning as WarningIcon, Clock,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import RackAutomationStrip from "@/components/RackAutomationStrip";

const STEPS = [
  { key: "price_notification",     label: "Price Notification",   route: "/price-notifications" },
  { key: "daily_readings",         label: "Daily Readings",       route: "/start-the-day/daily-readings" },
  { key: "tank_inventory",         label: "Tank Inventory",       route: "/start-the-day/tank-inventory" },
  { key: "reconciliation",         label: "Reconciliation",       route: "/start-the-day/reconciliation" },
];

const STATUS_META = {
  COMPLETE:    { tone: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200",
                 label: "Complete", Icon: CheckCircle, iconWeight: "fill" },
  IN_PROGRESS: { tone: "text-amber-600",   bg: "bg-amber-50 border-amber-200",
                 label: "In progress", Icon: DotOutline, iconWeight: "fill" },
  NOT_STARTED: { tone: "text-ink-secondary", bg: "bg-surface-alt border-divider",
                 label: "Not started", Icon: Circle, iconWeight: "regular" },
};

export default function StartTheDay() {
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.allSettled([
        api.get("/morning-operations/state"),
        api.get("/reconciliation/deliveries/summary"),
      ]);
      if (s.status === "fulfilled") setState(s.value.data);
      if (d.status === "fulfilled") setDeliveries(d.value.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const firstIncomplete = useMemo(() => {
    if (!state) return STEPS[0];
    return STEPS.find((s) => (state.steps?.[s.key]?.status || "NOT_STARTED") !== "COMPLETE") || null;
  }, [state]);

  const overall = state?.overall_status || "NOT_STARTED";
  const briefDate = state?.brief_date;
  const buttonLabel =
    overall === "COMPLETE"      ? "All morning steps complete"
  : overall === "IN_PROGRESS"   ? "Continue Start the Day"
  :                               "Start the Day";

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="start-the-day-page">
      <header className="flex items-start gap-3">
        <div className="rounded-md bg-brand/10 p-2 text-brand">
          <SunHorizon size={24} weight="fill" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink-primary">Morning Operations</h1>
          <p className="text-sm text-ink-secondary" data-testid="std-header-line">
            {briefDate
              ? `Daily Readings for ${formatDate(briefDate)} · Preparing operations for today`
              : "Preparing today's operations."}
          </p>
        </div>
      </header>

      {loading ? (
        <div className="text-sm text-ink-secondary" data-testid="std-loading">Loading…</div>
      ) : (
        <>
          <RackAutomationStrip />
          <ol className="rounded-lg border border-divider bg-surface" data-testid="std-steps">
            {STEPS.map((s, idx) => {
              const status = state?.steps?.[s.key]?.status || "NOT_STARTED";
              const meta = STATUS_META[status] || STATUS_META.NOT_STARTED;
              const Icon = meta.Icon;
              const isNext = firstIncomplete && firstIncomplete.key === s.key;
              return (
                <li key={s.key} className="border-b border-divider last:border-b-0">
                  <button
                    onClick={() => nav(s.route)}
                    data-testid={`std-step-${s.key}`}
                    className={[
                      "flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-surface-alt",
                      isNext ? "bg-brand/5" : "",
                    ].join(" ")}
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-alt text-sm font-semibold text-ink-secondary">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <div className="font-semibold text-ink-primary">{s.label}</div>
                      <div className={`text-[11px] font-semibold uppercase tracking-wide ${meta.tone}`}>
                        <Icon size={12} weight={meta.iconWeight} className="mr-1 inline align-[-1px]" />
                        {meta.label}
                      </div>
                    </div>
                    {isNext && overall !== "COMPLETE" && (
                      <span className="rounded-full bg-brand px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-foreground">
                        Next
                      </span>
                    )}
                    <CaretRight size={14} className="text-ink-muted" />
                  </button>
                </li>
              );
            })}
          </ol>

          {/* Sprint 10 · 2026-08-06 · PO directive — non-blocking
              awareness of yesterday's delivery pipeline. Informational
              only; does NOT introduce a new workflow step. */}
          {deliveries ? <YesterdayDeliveriesCard nav={nav} data={deliveries} /> : null}

          {/* Morning Brief is the outcome of the four steps, not a step. */}
          <button
            type="button"
            onClick={() => nav("/brief")}
            disabled={overall !== "COMPLETE"}
            data-testid="std-morning-brief-outcome"
            className={[
              "flex w-full items-center gap-4 rounded-lg border px-5 py-4 text-left transition",
              overall === "COMPLETE"
                ? "border-emerald-200 bg-emerald-50 hover:bg-emerald-100 cursor-pointer"
                : "border-divider bg-surface-alt opacity-70 cursor-not-allowed",
            ].join(" ")}
          >
            <span className={[
              "flex h-8 w-8 items-center justify-center rounded-full",
              overall === "COMPLETE"
                ? "bg-emerald-500 text-white"
                : "bg-surface text-ink-muted",
            ].join(" ")}>
              <Sun size={16} weight="fill" />
            </span>
            <div className="flex-1">
              <div className="font-semibold text-ink-primary">Morning Brief</div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-secondary">
                {overall === "COMPLETE"
                  ? "Ready — open the brief"
                  : "Unlocks after the four steps above are complete"}
              </div>
            </div>
            <CaretRight size={14} className="text-ink-muted" />
          </button>

          <div className="flex items-center justify-between rounded-lg border border-divider bg-surface-alt p-4">
            <div className="text-xs text-ink-secondary" data-testid="std-overall-status">
              Morning Status:{" "}
              <span className={`font-semibold uppercase tracking-wide ${
                overall === "COMPLETE" ? "text-emerald-600" :
                overall === "IN_PROGRESS" ? "text-amber-600" : "text-ink-secondary"
              }`}>
                {overall.replace("_", " ")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="std-preview-daily-package"
                onClick={() => nav(`/daily-package?date=${briefDate || new Date().toISOString().slice(0, 10)}`)}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
                title="Preview the printable Daily Operating Package"
              >
                Preview Daily Package
              </button>
              <button
                data-testid="std-continue"
                disabled={overall === "COMPLETE"}
                onClick={() => firstIncomplete && nav(firstIncomplete.route)}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground shadow-token-sm hover:bg-brand-hover disabled:opacity-50"
              >
                {buttonLabel}
                {overall !== "COMPLETE" && <CaretRight size={14} weight="bold" />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function YesterdayDeliveriesCard({ nav, data }) {
  const approved = data.approved || { count: 0, total_litres: "0", by_product: [] };
  const pending  = data.pending  || { count: 0, total_litres: "0", invoices: [] };
  const exception = data.exception || { count: 0, total_litres: "0", invoices: [] };
  const anyActivity = (approved.count + pending.count + exception.count) > 0;
  const needsAction = (pending.count + exception.count) > 0;
  // Amber = something needs operator attention (pending / exception).
  // Green  = all approved (with activity) — clean pipeline.
  // Neutral = no delivery activity for the day.
  const chip = needsAction
    ? { tone: "amber", label: `${pending.count + exception.count} to review`,
        cls: "border-amber-300 bg-amber-50 text-amber-800" }
    : approved.count > 0
    ? { tone: "green", label: "All approved",
        cls: "border-emerald-300 bg-emerald-50 text-emerald-800" }
    : { tone: "slate", label: "No activity",
        cls: "border-slate-200 bg-slate-50 text-slate-600" };
  const fmtL = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "0";
  };
  return (
    <section
      data-testid="std-yesterday-deliveries"
      className="rounded-lg border border-divider bg-surface"
    >
      <header className="flex items-center justify-between border-b border-divider px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Truck size={16} className="text-ink-secondary" weight="fill" />
          <div className="text-sm font-semibold text-ink-primary">Yesterday's Deliveries</div>
          <span className="text-[10px] uppercase tracking-wider text-ink-muted">Informational</span>
          <span
            data-testid={`std-yd-status-chip-${chip.tone}`}
            className={`ml-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${chip.cls}`}
            title={
              needsAction
                ? `${pending.count} pending · ${exception.count} exception — resolve in Receiving before Book Inventory is complete`
                : approved.count > 0
                ? "All supplier invoices for this day are approved and feeding Book Inventory"
                : "No delivery activity recorded for this day"
            }
          >
            <span
              aria-hidden
              className={`inline-block h-1.5 w-1.5 rounded-full ${
                chip.tone === "amber"
                  ? "bg-amber-500"
                  : chip.tone === "green"
                  ? "bg-emerald-500"
                  : "bg-slate-400"
              }`}
            />
            {chip.label}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => nav("/start-the-day/reconciliation")}
            data-testid="std-yd-view-deliveries"
            className="text-xs font-medium rounded border border-divider bg-surface px-2.5 py-1 hover:bg-surface-alt"
          >
            View Deliveries
          </button>
          <button
            type="button"
            onClick={() => nav("/receiving")}
            data-testid="std-yd-open-receiving"
            className="text-xs font-medium rounded border border-divider bg-surface px-2.5 py-1 hover:bg-surface-alt"
          >
            Open Receiving
          </button>
        </div>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-divider">
        <TileStat
          tone="emerald"
          Icon={CheckCircle}
          label="Approved Deliveries"
          count={approved.count}
          litres={fmtL(approved.total_litres)}
          testid="std-yd-approved"
        />
        <TileStat
          tone="slate"
          Icon={Clock}
          label="Pending Supplier Invoices"
          count={pending.count}
          litres={fmtL(pending.total_litres)}
          testid="std-yd-pending"
          onClick={pending.count > 0 ? () => nav("/receiving") : null}
        />
        <TileStat
          tone="amber"
          Icon={WarningIcon}
          label="Exception Receipts"
          count={exception.count}
          litres={fmtL(exception.total_litres)}
          testid="std-yd-exception"
          onClick={exception.count > 0 ? () => nav("/receiving/freight-exceptions") : null}
        />
      </div>
      {!anyActivity ? (
        <div className="border-t border-divider px-4 py-2 text-[11px] italic text-ink-secondary">
          No delivery activity recorded for {data.delivery_date}.
        </div>
      ) : null}
    </section>
  );
}

function TileStat({ tone, Icon, label, count, litres, testid, onClick }) {
  const toneCls = {
    emerald: "text-emerald-600",
    slate:   "text-slate-500",
    amber:   "text-amber-600",
  }[tone] || "text-ink-secondary";
  const interactive = typeof onClick === "function";
  const clickableCls = interactive
    ? "cursor-pointer hover:bg-surface-alt focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    : "";
  return (
    <div
      className={`px-4 py-3 transition-colors ${clickableCls}`}
      data-testid={testid}
      onClick={interactive ? onClick : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      title={interactive ? "Open in Receiving" : undefined}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-secondary">
        <Icon size={12} className={toneCls} weight="fill" />
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="font-mono text-lg font-semibold text-ink-primary tabular-nums">{count}</div>
        <div className="text-xs text-ink-secondary">
          {count === 1 ? "invoice" : "invoices"} · <span className="font-mono">{litres} L</span>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    // Sprint 10 · 2026-08-21 · See StartTheDayCapture.jsx for the
    // same fix. Local midnight (never UTC midnight) so operators
    // west of UTC never see the calendar day roll back by one.
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric",
    });
  } catch { return iso; }
}
